import type { RowData } from "@tanstack/react-table";
import type { DataGridCell, DataGridEditor } from "../columns/types";
import type { GridController } from "../focus/gridController";
import {
    findFocusableRowIndex,
    getNavigableColumnIds,
} from "../focus/navigation";
import type {
    DataGridCellAddress,
    DataGridCellChange,
    DataGridCellValue,
    DataGridInstance,
} from "../types";
import { getCell, getCoreCell, getEditableCell } from "./cellAccess";
import {
    getActiveRect,
    getBoundingRect,
    getRectCells,
    selectRect,
} from "./cellRect";
import {
    type ParseResult,
    parseEditorText,
    toEditorValue,
} from "./editorValues";

export interface EditContext<TData extends RowData> {
    instance: DataGridInstance<TData>;
    controller: GridController;
}

/** Where focus goes after a commit; `blur` leaves it wherever the user clicked. */
export type EditorMove = "up" | "down" | "left" | "right" | "stay" | "blur";

/** An editor's commit: typed `text` parses through the editor, a picked `value` commits as is. */
export type EditorCommit = { move: EditorMove } & (
    | { text: string }
    | { value: DataGridCellValue }
);

/** A change that writes `value` to `cell`, with the value it replaces as its editor reads it. */
export const toCellChange = <TData extends RowData>({
    cell,
    editor,
    value,
}: {
    cell: DataGridCell<TData>;
    editor: DataGridEditor;
    value: DataGridCellValue;
}): DataGridCellChange => ({
    rowId: cell.row.id,
    columnId: cell.column.id,
    value,
    previousValue: toEditorValue({ editor, value: cell.getValue() }),
});

/** One user action out: unchanged cells drop, the rest go out in one call and onto the undo stack. */
export const emitChanges = <TData extends RowData>(
    { instance }: EditContext<TData>,
    changes: readonly DataGridCellChange[],
) => {
    const effectiveChanges = changes.filter(
        (change) => change.value !== change.previousValue,
    );
    if (effectiveChanges.length === 0) {
        return;
    }
    instance.undoStack.record(effectiveChanges);
    instance.optionsRef.current.editing?.onCellsChange(effectiveChanges);
};

export const focusCellAt = <TData extends RowData>(
    { instance, controller }: EditContext<TData>,
    address: DataGridCellAddress,
) => {
    instance.table.setFocusedCell(address.rowId, address.columnId);
    controller.focusTarget({ ...address });
};

/** Opens the cell's editor; `seed` replaces the value with a typed key. */
export const startEdit = <TData extends RowData>(
    context: EditContext<TData>,
    { address, seed }: { address: DataGridCellAddress; seed: string | null },
) => {
    const { instance } = context;
    if (!getEditableCell(instance, getCell(instance.table, address))) {
        return false;
    }
    instance.atoms.activeEditor.set({ ...address, seed });
    return true;
};

export const cancelEdit = <TData extends RowData>(
    context: EditContext<TData>,
) => {
    const active = context.instance.atoms.activeEditor.get();
    context.instance.atoms.activeEditor.set(null);
    if (active) {
        focusCellAt(context, active);
    }
};

const STEPS: Record<
    Exclude<EditorMove, "blur">,
    { row: number; column: number }
> = {
    up: { row: -1, column: 0 },
    down: { row: 1, column: 0 },
    left: { row: 0, column: -1 },
    right: { row: 0, column: 1 },
    stay: { row: 0, column: 0 },
};

const moveFrom = <TData extends RowData>(
    context: EditContext<TData>,
    { address, move }: { address: DataGridCellAddress; move: EditorMove },
) => {
    if (move === "blur") {
        return;
    }
    const { table, rowKinds } = context.instance;
    const rows = table.getRowModel().rows;
    const columnIds = getNavigableColumnIds(table);
    const rowIndex = rows.findIndex((row) => row.id === address.rowId);
    if (rowIndex < 0) {
        return;
    }
    const columnIndex = columnIds.indexOf(address.columnId);
    const rowStep = STEPS[move].row;
    const columnStep = STEPS[move].column;
    const nextRowIndex =
        rowStep === 0
            ? rowIndex
            : findFocusableRowIndex({
                  rows,
                  isSeparator: rowKinds.isSeparator,
                  rowIndex: rowIndex + rowStep,
                  fromIndex: rowIndex,
              });
    const nextRow = rows[nextRowIndex] ?? rows[rowIndex];
    const nextColumnId =
        columnIds[columnIndex + columnStep] ?? address.columnId;
    focusCellAt(context, {
        rowId: nextRow?.id ?? address.rowId,
        columnId: nextColumnId,
    });
};

const parseCommit = ({
    editor,
    commit,
    today,
}: {
    editor: DataGridEditor;
    commit: EditorCommit;
    today: string | undefined;
}): ParseResult =>
    "value" in commit
        ? { ok: true, value: commit.value }
        : parseEditorText({ editor, text: commit.text, today });

/**
 * Commits the open editor's text or picked value. Returns the parse error to
 * show in the editor instead (a blur with invalid text reverts instead).
 */
export const commitEdit = <TData extends RowData>(
    context: EditContext<TData>,
    commit: EditorCommit,
): string | undefined => {
    const { instance } = context;
    const { move } = commit;
    const active = instance.atoms.activeEditor.get();
    if (!active) {
        return undefined;
    }
    const editable = getEditableCell(instance, getCell(instance.table, active));
    const parsed = editable
        ? parseCommit({
              editor: editable.editor,
              commit,
              today: instance.optionsRef.current.editing?.today,
          })
        : null;
    if (parsed && !parsed.ok && move !== "blur") {
        return parsed.message;
    }
    instance.atoms.activeEditor.set(null);
    if (editable && parsed?.ok) {
        emitChanges(context, [
            toCellChange({ ...editable, value: parsed.value }),
        ]);
    }
    moveFrom(context, { address: active, move });
    return undefined;
};

/** Delete and Backspace: empties every editable cell in the range. */
export const clearRange = <TData extends RowData>(
    context: EditContext<TData>,
) => {
    const { instance } = context;
    const rect = getActiveRect(instance.table);
    if (!rect) {
        return false;
    }
    const changes = getRectCells({ table: instance.table, rect })
        .flat()
        .flatMap((cell) => {
            const editable = getEditableCell(instance, cell);
            return editable ? [toCellChange({ ...editable, value: null })] : [];
        });
    emitChanges(context, changes);
    return true;
};

/**
 * Undo or redo: replays the stored changes and selects the cells they touched.
 * When the first cell's row is hidden (a search or page), the values still
 * apply and focus stays put. A step with a cell that is no longer editable is
 * refused whole and stays on the stack.
 */
export const replayHistory = <TData extends RowData>(
    context: EditContext<TData>,
    direction: "undo" | "redo",
) => {
    const { instance } = context;
    const { undoStack, optionsRef } = instance;
    const changes = undoStack.peek(direction);
    const first = changes?.[0];
    if (
        !changes ||
        !first ||
        changes.some(
            (change) =>
                !getEditableCell(instance, getCoreCell(instance.table, change)),
        )
    ) {
        return false;
    }
    if (direction === "undo") {
        undoStack.undo();
    } else {
        undoStack.redo();
    }
    optionsRef.current.editing?.onCellsChange(changes);
    const { table } = instance;
    if (!table.getRowModel().rows.some((row) => row.id === first.rowId)) {
        return true;
    }
    focusCellAt(context, first);
    const rect = getBoundingRect({ table, addresses: changes });
    if (rect) {
        selectRect({ table, rect });
    }
    return true;
};
